'use client';

import { useOverlayTriggerState } from '@react-stately/overlays';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';

import { ArrowDownSmall, ArrowUpSmall, Error } from '@zoomit/icons';
import { Box } from '@zoomit/primitives';
import type { KeySpecification} from '@zoomit/web-kit';
import { isMobile, KeySpecs } from '@zoomit/web-kit';
import { AlertBox, Button, cssVar, theme } from '@zoomit/zoomit-ds';

import type { ProductGroupSpecification } from '../../types/single-product';
import ProblemReportPopup from '../shared/ProblemReportPopup';
import SpecificationsContent from './SpecificationsContent';

type Props = {
  productTitle: string;
  id: string;
  keySpecifications: KeySpecification[];
  groupSpecs: ProductGroupSpecification[];
  boxTitle: string;
};

const TechnicalSpecifications = (props: Props) => {
  const { keySpecifications, productTitle, groupSpecs, id, boxTitle } = props;
  const detailsSectionState = useOverlayTriggerState({});

  useEffect(() => detailsSectionState.setOpen(false), [productTitle]);

  return (
    <>
      <div className="flex flex-col justify-between pt-2 lg:flex-row">
        <h2 className="border-primary1 text-h5 h-fit border-r-4 pr-4 text-right leading-6 lg:my-14 lg:basis-sm">
          {boxTitle}
        </h2>
        <div className="mt-0.5 flex grow flex-col items-center justify-between px-4 lg:items-start lg:px-0">
          <div className="flex w-full justify-between py-4 lg:py-14">
            <KeySpecs data={keySpecifications} />
            <Button
              variant={detailsSectionState.isOpen ? 'outline' : 'contained'}
              color={detailsSectionState.isOpen ? cssVar(theme.grey1) : cssVar(theme.white)}
              className="hidden !min-w-32 shrink-0 lg:block"
              rightIcon={
                detailsSectionState.isOpen ? <ArrowUpSmall size="sm" /> : <ArrowDownSmall size="sm" />
              }
              style={{ height: '3.2rem' }}
              onClick={detailsSectionState.toggle}
            >
              {detailsSectionState.isOpen ? 'بستن' : 'مشخصات کامل'}
            </Button>
          </div>
          <DetailsSection
            isOpen={detailsSectionState.isOpen}
            groupSpecs={groupSpecs}
            productTitle={productTitle}
            id={id}
          />

          <Button
            variant={detailsSectionState.isOpen ? 'outline' : 'contained'}
            color={detailsSectionState.isOpen ? cssVar(theme.grey1) : cssVar(theme.white)}
            className="!w-full shrink-0 lg:hidden"
            rightIcon={detailsSectionState.isOpen ? <ArrowUpSmall size="sm" /> : <ArrowDownSmall size="sm" />}
            style={{ height: '3.2rem' }}
            onClick={detailsSectionState.toggle}
          >
            {detailsSectionState.isOpen ? 'بستن' : 'مشخصات کامل'}
          </Button>

          {isMobile && detailsSectionState.isOpen && <DisclaimerAlert productTitle={productTitle} id={id} />}
        </div>
      </div>

      {/* This section is just for ssr */}
      <div className="sr-only">
        <SpecificationsContent groupSpecs={groupSpecs} productTitle={productTitle} />
      </div>
    </>
  );
};

const mobileContentVariants = {
  open: { maxHeight: 'unset' },
  collapsed: { maxHeight: 0 },
};

const desktopContentVariants = {
  open: { height: 'auto', overflow: 'initial' },
  collapsed: { height: 0, overflow: 'hidden' },
};

type DetailsSectionProps = {
  isOpen: boolean;
  groupSpecs: ProductGroupSpecification[];
  productTitle: string;
  id: string;
};

const DetailsSection = ({ isOpen, groupSpecs, productTitle, id }: DetailsSectionProps) => {
  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.section
          className="w-full"
          key="content"
          initial="collapsed"
          animate="open"
          exit="collapsed"
          variants={isMobile ? mobileContentVariants : desktopContentVariants}
        >
          <SpecificationsContent groupSpecs={groupSpecs} productTitle={productTitle} />
          {!isMobile && <DisclaimerAlert productTitle={productTitle} id={id} />}
        </motion.section>
      )}
    </AnimatePresence>
  );
};

type DisclaimerAlertProps = { productTitle: string; id: string };
const DisclaimerAlert = ({ id, productTitle }: DisclaimerAlertProps) => {
  const problemReportPopupState = useOverlayTriggerState({});

  return (
    <Box className="border-borderPrimary border-t py-4 md:border-none">
      <AlertBox icon={<Error fill={cssVar(theme.warning1)} />} variant="warning">
        <p className="text-body text-text m-0 w-auto leading-5">
          سلب مسئولیت: ممکن است بخشی از اطلاعات موجود در این صفحه درست نباشد. لطفا در صورت مشاهده هر گونه
          ایراد مشکل را
          <Button
            variant="text"
            className="!text-error !m-0 !w-fit !pr-2 !pl-0"
            onClick={problemReportPopupState.open}
          >
            گزارش کنید
          </Button>
          .
        </p>
      </AlertBox>
      <ProblemReportPopup
        productId={id}
        productTitle={productTitle}
        problemReportPopupState={problemReportPopupState}
      />
    </Box>
  );
};

export default TechnicalSpecifications;
